/**
 * Silent refresh scheduler.
 *
 * Schedules a refresh `LEAD_TIME_MS` before the current access token's `exp`.
 * When it fires we ask the client for a fresh token (which triggers the OIDC
 * refresh under the hood) and then re-schedule against the new expiry.
 *
 * ENG-2756 — retry-on-transient-failure. Access tokens live 15 minutes. The old
 * 30s lead time meant a refresh that failed transiently (a 5xx / network blip)
 * had ~30s of runway before `exp`, so "retry" bought almost nothing and the user
 * was signed out on any hiccup. LEAD_TIME_MS is now 5 minutes, and within that
 * window a transient failure (`TransientRefreshError` from
 * client.ts performRefresh) is retried with bounded exponential backoff. An
 * outage shorter than the window is absorbed invisibly; one that outlasts it ends
 * in teardown — performRefresh tears the session down on the first attempt made
 * after the token has actually expired (a DEFINITIVE error, handled below), so
 * the loop always terminates. A definitive failure (4xx / bad body) is NOT
 * retried: performRefresh has already torn the session down. Neither is the
 * post-teardown discard (a refresh that resolves AFTER signOut / emitAuthError —
 * ENG-2756 fix round 2): it rejects with a plain Error that arm()'s generation
 * guard swallows without re-arming, since that teardown already called cancel().
 *
 * One scheduler per client instance. signOut() must call cancel().
 */

/**
 * Refresh this many ms before `exp`. 5 minutes (ENG-2756): a token is 15 min, so
 * this leaves a 5-minute window to ride out a transient outage before the token
 * expires, instead of the ~30s the old value gave.
 */
export const LEAD_TIME_MS = 300_000;
/** Floor on any timer — never schedule shorter than this (avoid a hot-loop). */
const MIN_DELAY_MS = 1_000;
/** Ceiling — setTimeout cannot exceed ~24.8 days; clamp safely below. */
const MAX_DELAY_MS = 2 ** 31 - 1;
/** First transient-retry backoff (before jitter). */
const RETRY_BASE_MS = 2_000;
/** Cap on a single transient-retry backoff. Bounds the retry count within the
 *  5-minute window (~geometric), and bounds how far a final attempt can overshoot
 *  `exp` before performRefresh tears down. */
const RETRY_MAX_MS = 30_000;

/**
 * Thrown by client.ts performRefresh when a refresh failed for a reason that is
 * NOT proof the session is dead — a 5xx from /refresh, or a network failure —
 * while the current access token is still valid. The scheduler treats it as
 * "retry within the window"; every OTHER rejection is definitive (the session was
 * already torn down) and is not retried. Callers of the public `refreshSession()`
 * can `instanceof`-check it to tell a retryable outage from a real sign-out.
 */
export class TransientRefreshError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'TransientRefreshError';
  }
}

export interface SilentRefreshScheduler {
  /** Schedule the next refresh based on the given expiry (epoch seconds). */
  schedule(expiresAtSeconds: number): void;
  /** Cancel any pending refresh (idempotent). */
  cancel(): void;
}

function clampDelay(delayMs: number): number {
  return Math.max(MIN_DELAY_MS, Math.min(MAX_DELAY_MS, delayMs));
}

export function createSilentRefreshScheduler(
  refreshFn: () => Promise<void>,
  onError?: (err: unknown) => void,
): SilentRefreshScheduler {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  let expiresAtMs = 0;
  // Monotonic generation counter (ENG-2756 fix round). `timerId` alone cannot
  // stop an in-flight retry chain: once a timer fires, `arm()`'s callback sets
  // `timerId = null` BEFORE awaiting refreshFn, so a `cancel()` that lands while
  // the refresh is pending sees `timerId === null`, clears nothing, and the
  // rejection handler happily re-arms — the session then resurrects. A
  // concurrent `schedule()` had the mirror hole: `cancel()` cleared nothing, so
  // arming a fresh timer forked a SECOND chain. The fix: every `cancel()` (and
  // therefore every `schedule()`, which calls it) bumps this counter, and each
  // armed timer captures its value. A rejection handler whose captured value is
  // stale returns without re-arming, so a cancel/teardown/reschedule during an
  // in-flight refresh actually stops the old chain instead of letting it re-arm
  // or fork.
  let generation = 0;

  function cancel(): void {
    // Bump FIRST: this is the signal a still-in-flight chain reads to learn it
    // has been cancelled, even after its own fired timer nulled `timerId`.
    generation += 1;
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  // Equal-jitter exponential backoff: half fixed, half random, so retries
  // de-correlate across tabs/clients without ever dropping below MIN_DELAY_MS.
  function backoffDelay(): number {
    const exp = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (retryAttempt - 1));
    const jittered = exp / 2 + Math.random() * (exp / 2);
    return clampDelay(jittered);
  }

  function arm(delayMs: number): void {
    // Never leak a pending timer into a second concurrent chain. `schedule()`
    // already clears via `cancel()`, and the retry path arms only after the
    // fired callback nulled `timerId` — but if `arm()` is ever reached with a
    // live timer, drop it so two timers can't race.
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
    const armedGeneration = generation;
    timerId = setTimeout(() => {
      timerId = null;
      refreshFn().then(undefined, (err: unknown) => {
        // Cancelled or superseded while this refresh was in flight — a
        // `cancel()` teardown or a concurrent `schedule()` bumped the
        // generation. Do NOT re-arm: this is what makes `cancel()` able to stop
        // a chain whose timer has already fired (ENG-2756 fix round).
        if (armedGeneration !== generation) return;
        if (err instanceof TransientRefreshError) {
          // Terminate the retry loop once the token has expired. The scheduler is
          // a STANDALONE component and must not assume its refreshFn tears down on
          // expiry: this check is what guarantees the loop terminates regardless of
          // what refreshFn does — that is its own contract. (In the integrated
          // client a teardown fires first, and because every path that nulls
          // `currentSession` also calls scheduler.cancel(), the generation guard
          // above returns before reaching here — so this is not the integrated
          // loop's only stop.) Not defensive dead code: the scheduler unit test
          // (silent-refresh.test.ts) drives a refreshFn that ALWAYS throws
          // TransientRefreshError (never tears down) and asserts this stop fires
          // once the clock passes expiry.
          if (Date.now() >= expiresAtMs) {
            if (onError) onError(err);
            return;
          }
          retryAttempt += 1;
          arm(backoffDelay());
          return;
        }
        // Definitive failure (4xx, bad body, or the expiry teardown): the session
        // is already gone. Do not retry; surface for telemetry.
        if (onError) onError(err);
      });
    }, delayMs);
  }

  function schedule(expiresAtSeconds: number): void {
    // `cancel()` bumps the generation (invalidating any in-flight chain) and
    // clears any pending timer, so a `schedule()` concurrent with a running
    // retry cannot fork a second chain — the old one self-terminates.
    cancel();
    retryAttempt = 0;
    expiresAtMs = expiresAtSeconds * 1000;
    arm(clampDelay(expiresAtMs - Date.now() - LEAD_TIME_MS));
  }

  return { schedule, cancel };
}
